/**
 * Supervision client — mirrors `apps/api/src/domains/supervision/routes.ts`
 * verbatim. See that file's header for the permission model:
 *
 *   supervision:read   — see compliance (self-narrowed to "you and the people
 *                         you actually supervise" unless you hold `:manage`)
 *   supervision:record — record and correct sessions (only for a worker whose
 *                         `worker.supervisor_id` is really you, unless `:manage`)
 *   supervision:manage — change what the organization requires; org-wide read
 *
 * STATUS IS NEVER STORED. Every shape below is a SERVER-COMPUTED verdict —
 * there is no `compliant` boolean to send back. `status`/`statusLabel`/
 * `statusMeaning` always travel together so a screen never has to invent
 * copy for a status key it doesn't recognise.
 */
import { get, post } from './transport.js';
/** `GET /supervision/overview` — the subset the caller may see, summarized. */
export function overview(options) {
    return get('/supervision/overview', options);
}
/** `GET /supervision/workers` — the workforce compliance list, scoped server-side. */
export function workers(query = {}, options) {
    return get('/supervision/workers', { ...options, query: { ...query } });
}
/** `GET /supervision/workers/:workerId` — self, or someone you supervise, or org-wide with `:manage`. */
export function workerDetail(workerId, options) {
    return get(`/supervision/workers/${workerId}`, options);
}
/** `GET /supervision/requirements`. */
export async function requirements(query = {}, options) {
    const body = await get('/supervision/requirements', {
        ...options,
        query: { ...query },
    });
    return body.requirements;
}
/** `POST /supervision/requirements` — needs `supervision:manage`. */
export async function createRequirement(input, options) {
    const body = await post('/supervision/requirements', input, options);
    return body.requirement;
}
/**
 * `POST /supervision/requirements/:id/amend` — closes the existing window and
 * opens a new one. Never edits the row in place (see the route's own comment).
 */
export function amendRequirement(id, input, options) {
    return post(`/supervision/requirements/${id}/amend`, input, options);
}
/** `POST /supervision/requirements/:id/retire`. */
export async function retireRequirement(id, options) {
    const body = await post(`/supervision/requirements/${id}/retire`, {}, options);
    return body.requirement;
}
/**
 * `GET /supervision/sessions` — without `supervision:manage` the server scopes
 * this to the caller's own history plus that of the workers assigned to them,
 * the same edge `workerDetail` honours. A `workerId` outside that scope is
 * REFUSED (403); it is never silently replaced with the caller's own, which is
 * what this endpoint used to do.
 */
export function sessions(query = {}, options) {
    return get('/supervision/sessions', { ...options, query: { ...query } });
}
/** `POST /supervision/sessions` — needs `supervision:record`. */
export async function recordSession(input, options) {
    const body = await post('/supervision/sessions', input, options);
    return body.session;
}
/**
 * `POST /supervision/sessions/:id/correct` — writes a replacement and
 * supersedes the original; the original stays as history.
 */
export async function correctSession(id, input, options) {
    const body = await post(`/supervision/sessions/${id}/correct`, input, options);
    return body.session;
}
//# sourceMappingURL=supervision.js.map